// Block: Image Tabs
(function () {
	'use strict';

	// Breakpoints for responsive behavior
	const TABLET_BREAKPOINT = 1024;
	const DESKTOP_BREAKPOINT = 1200.99;

	// Image dimensions for aspect ratio calculations
	const IMAGE_WIDTH = 828;
	const IMAGE_HEIGHT = 523;

	// Store event listeners for cleanup
	const blockListeners = new WeakMap();

	// Initialize all image tabs blocks on the page
	function initImageTabs() {
		const imageTabsBlocks = document.querySelectorAll('.block-image-tabs');

		imageTabsBlocks.forEach((block) => {
			initBlock(block);
		});
	}

	// Determine active mode based on screen size and toggle setting
	function getActiveMode(block) {
		const screenWidth = window.innerWidth;
		const switchMode = block.getAttribute('data-switch-mode') || 'by-click';

		// Always use click for screens ≤1024px
		if (screenWidth <= TABLET_BREAKPOINT) {
			return 'click';
		}

		// For screens >1024px, use toggle value (by-click = click, by-hover = hover)
		return switchMode === 'by-click' ? 'click' : 'hover';
	}

	/**
	 * Check if viewport is desktop-and-up
	 * @return {boolean} True if viewport width is desktop or larger
	 */
	function isDesktop() {
		return window.innerWidth >= DESKTOP_BREAKPOINT;
	}

	/**
	 * Set aspect ratio on image element
	 * @param {Element} block
	 */
	function setAspectRatio(block) {
		const imageCol = block.querySelector('.block-image-tabs__image');
		const imagesCol = block.querySelector(
			'.block-image-tabs__images[data-dynamic-images="true"]'
		);

		if (imageCol) {
			imageCol.style.aspectRatio = IMAGE_WIDTH / IMAGE_HEIGHT;
		}

		if (imagesCol) {
			imagesCol.style.aspectRatio = IMAGE_WIDTH / IMAGE_HEIGHT;
		}
	}

	/**
	 * Set grid-template-columns based on layout type and settings
	 * @param {Element} block
	 */
	function setGridTemplateColumns(block) {
		const innerWrapper = block.querySelector('.block-image-tabs__inner');
		if (!innerWrapper) {
			return;
		}

		if (!isDesktop()) {
			innerWrapper.style.setProperty('--grid-template-columns', '');
			return;
		}

		const layoutType =
			innerWrapper.getAttribute('data-grid-layout-type') || 'chess';

		if (layoutType === 'chess') {
			const gap =
				parseFloat(innerWrapper.getAttribute('data-chess-gap')) || 0.8;
			innerWrapper.style.setProperty(
				'--grid-template-columns',
				`calc(50% - ${gap}rem) 1fr`
			);
		} else if (layoutType === 'proportional') {
			const col1 =
				parseFloat(
					innerWrapper.getAttribute('data-proportional-col1'),
					10
				) || 1;
			const col2 =
				parseFloat(
					innerWrapper.getAttribute('data-proportional-col2'),
					10
				) || 1;
			innerWrapper.style.setProperty(
				'--grid-template-columns',
				`${col1}fr ${col2}fr`
			);
		}
	}

	/**
	 * Sync grid row height with image column height so wrapper follows the image
	 * @param {Element} block
	 */
	function setWrapperHeight(block) {
		const innerWrapper = block.querySelector('.block-image-tabs__inner');
		if (!innerWrapper) {
			return;
		}

		if (!isDesktop()) {
			innerWrapper.style.gridTemplateRows = '';
			return;
		}

		const imageColumn =
			block.querySelector(
				'.block-image-tabs__images[data-dynamic-images="true"]'
			) || block.querySelector('.block-image-tabs__image');

		if (!imageColumn) {
			return;
		}

		const width = imageColumn.getBoundingClientRect().width;
		if (width <= 0) {
			return;
		}

		const rowHeight = width * (IMAGE_HEIGHT / IMAGE_WIDTH);
		innerWrapper.style.gridTemplateRows = rowHeight + 'px';
	}

	/**
	 * Attach one-time image load handlers to ensure height sync after assets load
	 * @param {Element} block
	 */
	function observeImages(block) {
		if (block.dataset.imagesObserved === 'true') {
			return;
		}

		const images = block.querySelectorAll(
			'.block-image-tabs__image img, .block-image-tabs__images img'
		);

		images.forEach((img) => {
			if (img.complete) {
				return;
			}

			img.addEventListener(
				'load',
				() => {
					setWrapperHeight(block);
				},
				{ once: true }
			);
		});

		block.dataset.imagesObserved = 'true';
	}

	// Remove existing event listeners from a block
	function removeEventListeners(block) {
		const listeners = blockListeners.get(block);
		if (listeners) {
			listeners.forEach(({ element, event, handler }) => {
				element.removeEventListener(event, handler);
			});
			blockListeners.delete(block);
		}
	}

	// Attach event listeners based on active mode
	function attachEventListeners(block, mode, hasDynamicImages) {
		const tabButtons = block.querySelectorAll(
			'.block-image-tabs__tab-button'
		);
		const listeners = [];

		tabButtons.forEach((button, index) => {
			if (mode === 'hover') {
				// Use mouseenter for hover mode
				const hoverHandler = () => {
					setActiveTab(block, index, hasDynamicImages);
				};
				button.addEventListener('mouseenter', hoverHandler);
				listeners.push({
					element: button,
					event: 'mouseenter',
					handler: hoverHandler,
				});
			} else {
				// Use click for click mode
				const clickHandler = () => {
					setActiveTab(block, index, hasDynamicImages);
				};
				button.addEventListener('click', clickHandler);
				listeners.push({
					element: button,
					event: 'click',
					handler: clickHandler,
				});
			}
		});

		blockListeners.set(block, listeners);
	}

	// Initialize a single block
	function initBlock(block) {
		const tabButtons = block.querySelectorAll(
			'.block-image-tabs__tab-button'
		);
		const tabPanels = block.querySelectorAll(
			'.block-image-tabs__tab-panel'
		);

		if (tabButtons.length === 0 || tabPanels.length === 0) {
			return;
		}

		// Check if dynamic images are enabled
		const hasDynamicImages =
			block.querySelector(
				'.block-image-tabs__images[data-dynamic-images="true"]'
			) !== null;

		// Set first tab as active by default
		setActiveTab(block, 0, hasDynamicImages);

		// Ensure first image is active if dynamic images are enabled
		if (hasDynamicImages) {
			const images = block.querySelectorAll(
				'.block-image-tabs__images[data-dynamic-images="true"] img'
			);
			images.forEach((img, index) => {
				if (index === 0) {
					img.classList.add('block-image-tabs__image--active');
					img.classList.remove('block-image-tabs__image--hidden');
				} else {
					img.classList.add('block-image-tabs__image--hidden');
					img.classList.remove('block-image-tabs__image--active');
				}
			});
		}

		// Remove any existing listeners
		removeEventListeners(block);

		// Determine active mode and attach appropriate listeners
		const activeMode = getActiveMode(block);
		attachEventListeners(block, activeMode, hasDynamicImages);
		setAspectRatio(block);
		setGridTemplateColumns(block);
		setWrapperHeight(block);
		observeImages(block);
	}

	// Set active tab
	function setActiveTab(block, tabIndex, hasDynamicImages) {
		const tabButtons = block.querySelectorAll(
			'.block-image-tabs__tab-button'
		);
		const tabPanels = block.querySelectorAll(
			'.block-image-tabs__tab-panel'
		);

		// Remove active class from all buttons and panels
		tabButtons.forEach((button) => {
			button.classList.remove('block-image-tabs__tab-button--active');
			button.setAttribute('tabindex', '-1');
			button.setAttribute('aria-selected', 'false');
		});

		tabPanels.forEach((panel) => {
			panel.classList.remove('block-image-tabs__tab-panel--active');
			panel.setAttribute('hidden', '');
		});

		// Add active class to selected button and panel
		if (tabButtons[tabIndex] && tabPanels[tabIndex]) {
			tabButtons[tabIndex].classList.add(
				'block-image-tabs__tab-button--active'
			);
			tabButtons[tabIndex].setAttribute('tabindex', '0');
			tabButtons[tabIndex].setAttribute('aria-selected', 'true');

			tabPanels[tabIndex].classList.add(
				'block-image-tabs__tab-panel--active'
			);
			tabPanels[tabIndex].removeAttribute('hidden');
		}

		// Switch image if dynamic images are enabled
		if (hasDynamicImages) {
			const images = block.querySelectorAll(
				'.block-image-tabs__images[data-dynamic-images="true"] img'
			);

			images.forEach((img, index) => {
				if (index === tabIndex) {
					// Show active image
					img.classList.remove('block-image-tabs__image--hidden');
					img.classList.add('block-image-tabs__image--active');
				} else {
					// Hide inactive images
					img.classList.remove('block-image-tabs__image--active');
					img.classList.add('block-image-tabs__image--hidden');
				}
			});
		}

		setGridTemplateColumns(block);
		setWrapperHeight(block);
	}

	// Handle window resize to re-evaluate mode when crossing breakpoint
	let resizeTimeout;
	function handleResize() {
		clearTimeout(resizeTimeout);
		resizeTimeout = setTimeout(() => {
			const imageTabsBlocks =
				document.querySelectorAll('.block-image-tabs');
			imageTabsBlocks.forEach((block) => {
				const tabButtons = block.querySelectorAll(
					'.block-image-tabs__tab-button'
				);
				const tabPanels = block.querySelectorAll(
					'.block-image-tabs__tab-panel'
				);

				if (tabButtons.length === 0 || tabPanels.length === 0) {
					return;
				}

				const hasDynamicImages =
					block.querySelector(
						'.block-image-tabs__images[data-dynamic-images="true"]'
					) !== null;
				const previousMode = block.dataset.currentMode || '';
				const currentMode = getActiveMode(block);

				// Only re-initialize if mode changed
				if (previousMode !== currentMode) {
					block.dataset.currentMode = currentMode;
					removeEventListeners(block);
					attachEventListeners(block, currentMode, hasDynamicImages);
				}

				setGridTemplateColumns(block);
				setWrapperHeight(block);
			});
		}, 150);
	}

	// Initialize when DOM is ready
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initImageTabs);
	} else {
		initImageTabs();
	}

	// Add resize listener for responsive behavior
	window.addEventListener('resize', handleResize);

	// Re-initialize for dynamically loaded content (if needed)
	if (typeof window !== 'undefined') {
		window.initImageTabs = initImageTabs;
	}
})();
